import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { keycloakConfig, KEYCLOAK_TOKEN_URL } from "../config/authConfig";
import { parseKeycloakProfile, hasProfileChanged } from "../utils/keycloakProfile";

export const ACCESS_TOKEN_LIFETIME_SEC = 300;
export const REFRESH_TOKEN_LIFETIME_SEC = 1800;
export const REFRESH_BEFORE_EXPIRY_SEC = 30;
const REFRESH_CHECK_INTERVAL_MS = 1000;

const SYNC_STORAGE_KEY = "keycloak_token_sync_v1";
const LEADER_STORAGE_KEY = "keycloak_refresh_leader_v1";
const LOGOUT_STORAGE_KEY = "keycloak_logout_v1";
const LOCK_NAME = "keycloak-token-refresh";
const LEADER_TIMEOUT_MS = 4000;
const BROADCAST_CHANNEL_NAME = "keycloak_token_refresh";
const TAB_ID = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

let refreshInFlightPromise = null;
let refreshCheckIntervalId = null;
let sessionInitialized = false;
let isLeaderTab = false;
let handlingRemoteLogout = false;
let currentRefreshToken = null;
let currentExpiresAt = null;
/** Absolute unix timestamp (sec) when the next access-token refresh should run. */
let nextRefreshAt = null;
let currentUserProfile = null;
let sessionCallbacks = null;
let lastAppliedUpdatedAt = 0;
let broadcastChannel = null;
let storageListener = null;

function nowSec() {
  return Math.floor(Date.now() / 1000);
}

function updateNextRefreshAt(expiresAt) {
  if (!expiresAt) {
    nextRefreshAt = null;
    return;
  }
  nextRefreshAt = expiresAt - REFRESH_BEFORE_EXPIRY_SEC;
}

function isRefreshDue(expiresAt) {
  if (!expiresAt) {
    return true;
  }
  const now = nowSec();
  return now >= expiresAt - REFRESH_BEFORE_EXPIRY_SEC || now >= expiresAt;
}

function shouldRefreshAccessToken() {
  if (!currentRefreshToken) {
    return false;
  }

  return isRefreshDue(currentExpiresAt);
}

function readJson(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

function readSharedTokens() {
  return readJson(SYNC_STORAGE_KEY);
}

function readLeader() {
  return readJson(LEADER_STORAGE_KEY);
}

function writeLeaderHeartbeat() {
  writeJson(LEADER_STORAGE_KEY, {
    tabId: TAB_ID,
    heartbeatAt: Date.now(),
  });
}

function releaseLeadership() {
  try {
    const leader = readLeader();
    if (leader?.tabId === TAB_ID) {
      localStorage.removeItem(LEADER_STORAGE_KEY);
    }
  } catch (error) {
    // ignore
  }
  isLeaderTab = false;
}

/**
 * Only one tab should refresh. Leader is claimed via localStorage heartbeat.
 * Stale leaders (closed/crashed tabs) are taken over after LEADER_TIMEOUT_MS.
 */
function ensureLeadership() {
  const now = Date.now();
  const leader = readLeader();

  if (
    leader?.tabId &&
    leader.tabId !== TAB_ID &&
    now - leader.heartbeatAt <= LEADER_TIMEOUT_MS
  ) {
    isLeaderTab = false;
    return false;
  }

  writeLeaderHeartbeat();

  // Last writer wins; confirm we still own the key after the write.
  const verified = readLeader();
  isLeaderTab = verified?.tabId === TAB_ID;
  return isLeaderTab;
}

function sharedPayloadToTokens(shared) {
  return {
    access_token: shared.access_token,
    refresh_token: shared.refresh_token,
    id_token: shared.id_token || null,
    expires_in: shared.expires_in,
    refresh_expires_in: shared.refresh_expires_in,
    ...(shared.profile && {
      email: shared.email,
      username: shared.username,
      profile: shared.profile,
    }),
  };
}

function applySharedTokens(shared, { notify = true } = {}) {
  if (!shared?.refresh_token || !shared?.expires_at || !shared?.updatedAt) {
    return false;
  }

  if (shared.updatedAt <= lastAppliedUpdatedAt) {
    return false;
  }

  lastAppliedUpdatedAt = shared.updatedAt;
  currentRefreshToken = shared.refresh_token;
  currentExpiresAt = shared.expires_at;
  updateNextRefreshAt(currentExpiresAt);

  if (shared.profile) {
    currentUserProfile = {
      email: shared.email,
      username: shared.username,
      profile: shared.profile,
    };
  }

  if (notify && sessionCallbacks && sessionInitialized) {
    sessionCallbacks.onTokensUpdated(sharedPayloadToTokens(shared));
  }

  return true;
}

function isFatalRefreshError(error) {
  const status = error?.response?.status;
  const errorCode =
    error?.response?.data?.error ||
    error?.response?.data?.error_description ||
    "";

  // Only end the session for definitive auth failures from Keycloak.
  if (status === 400 || status === 401) {
    return true;
  }

  if (
    typeof errorCode === "string" &&
    /invalid_grant|invalid_token|expired/i.test(errorCode)
  ) {
    return true;
  }

  return false;
}

function publishSharedTokens(tokens) {
  const updatedAt = Date.now();
  const expiresAt = nowSec() + tokens.expires_in;
  const previousShared = readSharedTokens();
  const shared = {
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token,
    // Refresh responses often omit id_token — keep the previous one for logout.
    id_token: tokens.id_token || previousShared?.id_token || null,
    expires_in: tokens.expires_in,
    refresh_expires_in: tokens.refresh_expires_in,
    expires_at: expiresAt,
    updatedAt,
    tabId: TAB_ID,
    ...(tokens.profile && {
      email: tokens.email,
      username: tokens.username,
      profile: tokens.profile,
    }),
  };

  lastAppliedUpdatedAt = updatedAt;
  currentRefreshToken = shared.refresh_token;
  currentExpiresAt = shared.expires_at;
  updateNextRefreshAt(currentExpiresAt);

  try {
    writeJson(SYNC_STORAGE_KEY, shared);
  } catch (error) {
    console.error("Failed to persist shared Keycloak tokens:", error);
  }

  if (broadcastChannel) {
    try {
      broadcastChannel.postMessage({ type: "tokens_updated", payload: shared });
    } catch (error) {
      // Channel may be closed during teardown.
    }
  }

  return shared;
}

function syncFromSharedTokens() {
  const shared = readSharedTokens();
  if (!shared) {
    return false;
  }

  return applySharedTokens(shared, { notify: true });
}

async function withRefreshLock(task) {
  if (typeof navigator !== "undefined" && navigator.locks?.request) {
    return navigator.locks.request(LOCK_NAME, { mode: "exclusive" }, task);
  }

  // Fallback when Web Locks is unavailable: only the elected leader may run.
  if (!isLeaderTab && !ensureLeadership()) {
    return null;
  }

  return task();
}

async function fetchAccessToken(refreshToken) {
  const params = new URLSearchParams();
  params.append("grant_type", "refresh_token");
  params.append("client_id", keycloakConfig.client_id);
  params.append("client_secret", keycloakConfig.client_secret);
  params.append("refresh_token", refreshToken);

  const response = await axios.post(KEYCLOAK_TOKEN_URL, params, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });
  const currentProfile = parseKeycloakProfile(
    jwtDecode(response.data.access_token)
  );
  const profileChanged = hasProfileChanged(currentUserProfile, currentProfile);

  if (profileChanged) {
    currentUserProfile = currentProfile;
  }

  return {
    access_token: response.data.access_token,
    refresh_token: response.data.refresh_token || refreshToken,
    id_token: response.data.id_token || null,
    expires_in: response.data.expires_in || ACCESS_TOKEN_LIFETIME_SEC,
    refresh_expires_in:
      response.data.refresh_expires_in || REFRESH_TOKEN_LIFETIME_SEC,
    ...(profileChanged && {
      email: currentProfile.email,
      username: currentProfile.username,
      profile: currentProfile.profile,
    }),
  };
}

async function refreshAccessTokenLocked(refreshToken) {
  const result = await withRefreshLock(async () => {
    // Another tab may have refreshed while we waited for the lock.
    const shared = readSharedTokens();
    if (shared && !isRefreshDue(shared.expires_at)) {
      applySharedTokens(shared, { notify: true });
      return { tokens: sharedPayloadToTokens(shared), fetched: false };
    }

    const tokenToUse = shared?.refresh_token || refreshToken;
    const tokens = await fetchAccessToken(tokenToUse);
    publishSharedTokens(tokens);

    if (sessionCallbacks && sessionInitialized) {
      sessionCallbacks.onTokensUpdated(tokens);
    }

    return { tokens, fetched: true };
  });

  if (result == null) {
    syncFromSharedTokens();
    return null;
  }

  return result.tokens;
}

export async function refreshAccessToken(refreshToken) {
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  if (refreshInFlightPromise) {
    return refreshInFlightPromise;
  }

  refreshInFlightPromise = refreshAccessTokenLocked(refreshToken).finally(() => {
    refreshInFlightPromise = null;
  });

  return refreshInFlightPromise;
}

export function isAccessTokenValid(expiresAt) {
  if (!expiresAt) {
    return false;
  }

  return expiresAt > nowSec();
}

export function getAccessTokenExpiresIn(expiresAt) {
  if (!expiresAt) {
    return ACCESS_TOKEN_LIFETIME_SEC;
  }

  return Math.max(expiresAt - nowSec(), 0);
}

function clearRefreshChecker() {
  if (refreshCheckIntervalId) {
    clearInterval(refreshCheckIntervalId);
    refreshCheckIntervalId = null;
  }
}

function detachCrossTabListeners() {
  if (broadcastChannel) {
    try {
      broadcastChannel.close();
    } catch (error) {
      // ignore
    }
    broadcastChannel = null;
  }

  if (storageListener) {
    window.removeEventListener("storage", storageListener);
    storageListener = null;
  }
}

function handleRemoteLogoutSignal(payload) {
  if (handlingRemoteLogout || !sessionInitialized) {
    return;
  }

  // Ignore our own logout echo if it ever arrives.
  if (payload?.tabId === TAB_ID) {
    return;
  }

  handlingRemoteLogout = true;
  const onRemoteLogout = sessionCallbacks?.onRemoteLogout;

  clearRefreshChecker();
  detachCrossTabListeners();
  releaseLeadership();
  sessionInitialized = false;
  currentRefreshToken = null;
  currentExpiresAt = null;
  nextRefreshAt = null;
  currentUserProfile = null;
  sessionCallbacks = null;
  refreshInFlightPromise = null;
  lastAppliedUpdatedAt = 0;

  if (onRemoteLogout) {
    onRemoteLogout();
  }

  handlingRemoteLogout = false;
}

function clearSharedSessionStorage() {
  try {
    localStorage.removeItem(SYNC_STORAGE_KEY);
    localStorage.removeItem(LEADER_STORAGE_KEY);
  } catch (error) {
    // ignore
  }
}

/**
 * Clear the cross-tab logout marker. Must be called on intentional login so a
 * prior same-browser logout (which wrote this key, then reloaded with a new
 * TAB_ID) is not treated as a remote logout when the refresh session starts.
 */
export function clearLogoutBroadcast() {
  try {
    localStorage.removeItem(LOGOUT_STORAGE_KEY);
  } catch (error) {
    // ignore
  }
}

/**
 * Notify every other open tab to log out locally.
 * The initiating tab should still run Keycloak signoutRedirect.
 */
export function broadcastLogout() {
  const payload = { tabId: TAB_ID, at: Date.now() };

  clearSharedSessionStorage();

  try {
    writeJson(LOGOUT_STORAGE_KEY, payload);
  } catch (error) {
    console.error("Failed to broadcast logout:", error);
  }

  const message = { type: "logout", payload };

  if (broadcastChannel) {
    try {
      broadcastChannel.postMessage(message);
    } catch (error) {
      // Channel may already be closing.
    }
  } else if (typeof BroadcastChannel !== "undefined") {
    try {
      const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      channel.postMessage(message);
      channel.close();
    } catch (error) {
      // ignore
    }
  }

  stopRefreshSession();
}

function attachCrossTabListeners() {
  detachCrossTabListeners();

  if (typeof BroadcastChannel !== "undefined") {
    broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    broadcastChannel.onmessage = (event) => {
      if (event?.data?.type === "logout") {
        handleRemoteLogoutSignal(event.data.payload);
        return;
      }

      if (event?.data?.type === "tokens_updated" && event.data.payload) {
        applySharedTokens(event.data.payload, { notify: true });
      }
    };
  }

  storageListener = (event) => {
    if (event.key === LOGOUT_STORAGE_KEY && event.newValue) {
      try {
        handleRemoteLogoutSignal(JSON.parse(event.newValue));
      } catch (error) {
        // ignore malformed payload
      }
      return;
    }

    if (event.key === SYNC_STORAGE_KEY && event.newValue) {
      try {
        applySharedTokens(JSON.parse(event.newValue), { notify: true });
      } catch (error) {
        // ignore malformed payload
      }
      return;
    }

    if (event.key === LEADER_STORAGE_KEY) {
      const leader = event.newValue ? readLeader() : null;
      if (!leader || leader.tabId !== TAB_ID) {
        isLeaderTab = false;
      }
    }
  };
  window.addEventListener("storage", storageListener);
}

async function checkAndRefreshIfDue() {
  if (!sessionInitialized || !currentRefreshToken) {
    return;
  }

  // Catch logout broadcasts missed while the tab was backgrounded/frozen.
  const logoutEvent = readJson(LOGOUT_STORAGE_KEY);
  if (
    logoutEvent?.at &&
    logoutEvent.tabId !== TAB_ID &&
    Date.now() - logoutEvent.at < 5 * 60 * 1000
  ) {
    handleRemoteLogoutSignal(logoutEvent);
    return;
  }

  // Every tab keeps local Redux state in sync with the shared tokens.
  syncFromSharedTokens();

  if (refreshInFlightPromise) {
    return;
  }

  // Only the leader tab is allowed to call the token API.
  if (!ensureLeadership()) {
    return;
  }

  if (!shouldRefreshAccessToken()) {
    return;
  }

  // Re-check shared state right before calling — another leader race may have won.
  const shared = readSharedTokens();
  if (shared && !isRefreshDue(shared.expires_at)) {
    applySharedTokens(shared, { notify: true });
    return;
  }

  try {
    await refreshAccessToken(currentRefreshToken);
  } catch (error) {
    console.error("Keycloak token refresh failed:", error);

    // Another tab may have rotated the refresh token while this call failed.
    syncFromSharedTokens();
    const sharedAfterError = readSharedTokens();
    if (sharedAfterError && !isRefreshDue(sharedAfterError.expires_at)) {
      return;
    }

    // Transient network / 5xx errors should not auto-logout the dashboard.
    if (!isFatalRefreshError(error)) {
      return;
    }

    if (sessionCallbacks) {
      sessionCallbacks.onError(error);
    }
  }
}

function startRefreshChecker() {
  clearRefreshChecker();
  ensureLeadership();
  checkAndRefreshIfDue();
  refreshCheckIntervalId = setInterval(() => {
    checkAndRefreshIfDue();
  }, REFRESH_CHECK_INTERVAL_MS);
}

export function startRefreshSession({
  refreshToken,
  expiresAt,
  profile,
  email,
  username,
  onTokensUpdated,
  onError,
  onRemoteLogout,
}) {
  if (sessionInitialized || refreshInFlightPromise) {
    return;
  }

  if (!refreshToken) {
    onError(new Error("No refresh token available"));
    return;
  }

  sessionInitialized = true;
  handlingRemoteLogout = false;
  currentRefreshToken = refreshToken;
  currentExpiresAt = expiresAt || null;
  updateNextRefreshAt(currentExpiresAt);
  currentUserProfile = profile
    ? { email, username, profile }
    : null;
  sessionCallbacks = { onTokensUpdated, onError, onRemoteLogout };

  // Starting a session means this tab is intentionally logged in. Clear any
  // leftover logout marker from a prior logout+reload in this browser — that
  // path gets a new TAB_ID, so treating the stale key as "remote logout" would
  // immediately bounce the user back to /login after a successful token fetch.
  // Live cross-tab logout still works via BroadcastChannel / storage events.
  clearLogoutBroadcast();

  const shared = readSharedTokens();
  if (shared?.refresh_token) {
    applySharedTokens(shared, { notify: true });
  }

  attachCrossTabListeners();
  startRefreshChecker();
}

export function stopRefreshSession() {
  clearRefreshChecker();
  detachCrossTabListeners();
  releaseLeadership();
  sessionInitialized = false;
  currentRefreshToken = null;
  currentExpiresAt = null;
  nextRefreshAt = null;
  currentUserProfile = null;
  sessionCallbacks = null;
  refreshInFlightPromise = null;
  lastAppliedUpdatedAt = 0;
}

export function isRefreshSessionActive() {
  return sessionInitialized;
}

export function getNextRefreshAt() {
  return nextRefreshAt;
}

export function isRefreshLeaderTab() {
  return isLeaderTab;
}
